import { useEffect } from "react";
import { env } from "./env";
import { NAV_ITEMS } from "./nav";
import { chromeFor } from "@/content/chrome";
import type { Locale } from "@/i18n/locales";

interface BreadcrumbItem {
  name: string;
  url: string;
}

const BREADCRUMB_SCRIPT_ID = "ld-breadcrumb";
const SITENAV_SCRIPT_ID = "ld-sitenav";
const WEBPAGE_SCRIPT_ID = "ld-webpage";

const ORG_ID = `${env.publicDomain}/#org`;
const WEBSITE_ID = `${env.publicDomain}/#website`;

export function useBreadcrumbStructuredData(items: ReadonlyArray<BreadcrumbItem>): void {
  const key = JSON.stringify(items);

  useEffect(() => {
    const parsed = JSON.parse(key) as ReadonlyArray<BreadcrumbItem>;
    const script = ensureScript(BREADCRUMB_SCRIPT_ID);

    const payload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: parsed.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: item.name,
        item: item.url.startsWith("http") ? item.url : `${env.publicDomain}${item.url}`,
      })),
    };
    script.textContent = JSON.stringify(payload);

    return () => {
      script.remove();
    };
  }, [key]);
}

// Signals primary-navigation structure to search-engine crawlers; one of
// the candidate signals Google uses when deciding which links to surface
// as sitelinks under a brand SERP result. Wired once at the App root so
// the script ships on every route. ItemList wrapper + position fields
// gives the most-validated shape for Google's parsers.
// The locale arrives as an argument rather than from `useLocale()` because the
// App root calls this hook ABOVE the LocaleProvider it renders; a context read
// here would silently resolve to the default and emit English names on a German
// page — a wrong value, not a crash, which nothing downstream would surface.
export function useSiteNavigationStructuredData(locale: Locale): void {
  useEffect(() => {
    const script = ensureScript(SITENAV_SCRIPT_ID);
    const labels = chromeFor(locale).nav.labels;

    const payload = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Primary navigation",
      itemListElement: NAV_ITEMS.map((item, idx) => ({
        "@type": "SiteNavigationElement",
        position: idx + 1,
        name: labels[item.id],
        url: `${env.publicDomain}${item.id}`,
      })),
    };
    script.textContent = JSON.stringify(payload);

    return () => {
      script.remove();
    };
  }, [locale]);
}

// Wraps the Landing route's content in a WebPage entity whose mainEntity
// is an ItemList of the three primary Service offerings. Each Service
// back-references the Organization @id so Google's entity graph stitches
// offerings to the brand. Wired only on Landing because the other routes
// already carry per-route BreadcrumbList JSON-LD.
export function useLandingStructuredData(): void {
  useEffect(() => {
    const script = ensureScript(WEBPAGE_SCRIPT_ID);

    const payload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${env.publicDomain}/#webpage-home`,
      url: `${env.publicDomain}/`,
      name: "Flex Fund — MT5 Prop Firm",
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": ORG_ID },
      mainEntity: {
        "@type": "ItemList",
        name: "Flex Fund primary offerings",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "Service",
              name: "Funded Trader Challenge",
              description:
                "1-step or 2-step evaluation to a funded trading account up to $400k with 80-90% profit split.",
              provider: { "@id": ORG_ID },
              serviceType: "Proprietary trading evaluation",
              url: `${env.publicDomain}/how-it-works`,
              areaServed: "Worldwide",
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@type": "Service",
              name: "Evaluation Account",
              description:
                "MetaTrader 5 evaluation account with real-market execution, simulating live trading conditions for the challenge.",
              provider: { "@id": ORG_ID },
              serviceType: "Proprietary trading evaluation",
              url: `${env.publicDomain}/how-it-works`,
              areaServed: "Worldwide",
            },
          },
          {
            "@type": "ListItem",
            position: 3,
            item: {
              "@type": "Service",
              name: "MetaTrader 5 Platform",
              description:
                "Trade real markets on Flex Fund's MetaTrader 5 infrastructure with institutional-grade execution.",
              provider: { "@id": ORG_ID },
              serviceType: "Trading platform access",
              url: `${env.publicDomain}/platforms`,
              areaServed: "Worldwide",
            },
          },
        ],
      },
    };
    script.textContent = JSON.stringify(payload);

    return () => {
      script.remove();
    };
  }, []);
}

function ensureScript(id: string): HTMLScriptElement {
  const existing = document.head.querySelector<HTMLScriptElement>(`script[id="${id}"]`);
  if (existing) return existing;
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  document.head.appendChild(script);
  return script;
}
